import { FliptClient } from "@flipt-io/flipt";
import type {
	BatchEvaluationResponse,
	BooleanEvaluationResponse,
	VariantEvaluationResponse,
} from "@flipt-io/flipt/dist/evaluation/models";
import { nanoid } from "nanoid";
import type {
	FeatureFlagRequest,
	FeatureFlagsOptions,
	FeatureFlagType,
} from "./types";

/**
 * Flipt evaluation for browser apps. Each consumer builds one with its own URL,
 * namespace and evaluation context, so the client itself carries no knowledge
 * of any app's auth or config modules.
 */
export class FeatureFlagClient {
	constructor(private readonly options: FeatureFlagsOptions) {}

	get isDisabled(): boolean {
		return this.options.disabled === true;
	}

	/**
	 * Built per call rather than once per client: `options.url` is read from the
	 * environment at construction time, and evaluation can be requested before
	 * the app has finished bootstrapping.
	 */
	private createClient(): FliptClient {
		return new FliptClient({ url: this.options.url });
	}

	private buildRequest(flagKey: string) {
		return {
			flagKey,
			namespaceKey: this.options.namespace,
			entityId: nanoid(),
			context: this.options.getContext?.() ?? {},
		};
	}

	/**
	 * Raw evaluation: it does not honour `disabled` and it propagates errors, so
	 * bootstrap code can tell "flag off" apart from "Flipt unreachable".
	 */
	async evaluate<T extends FeatureFlagType = "boolean">(
		flagKey: string,
		flagType: FeatureFlagType = "boolean",
	): Promise<
		T extends "boolean" ? BooleanEvaluationResponse : VariantEvaluationResponse
	> {
		const response = await this.createClient().evaluation[flagType](
			this.buildRequest(flagKey),
		);

		return response as T extends "boolean"
			? BooleanEvaluationResponse
			: VariantEvaluationResponse;
	}

	async evaluateBatch(
		requests: FeatureFlagRequest[],
	): Promise<BatchEvaluationResponse> {
		return await this.createClient().evaluation.batch({
			requests: requests.map(({ flagKey }) => this.buildRequest(flagKey)),
		});
	}

	/** Fails closed: an unreachable or misconfigured Flipt reports the flag off. */
	async isEnabled(flagKey: string): Promise<boolean> {
		if (this.isDisabled) {
			return false;
		}

		try {
			const evaluation = await this.evaluate(flagKey);
			return evaluation.enabled;
		} catch {
			return false;
		}
	}

	/** Fails closed as a whole: one failed batch reports every flag off. */
	async getEnabledFlags<K extends string>(
		flagKeys: readonly K[],
	): Promise<Partial<Record<K, boolean>>> {
		if (this.isDisabled) {
			return {};
		}

		try {
			const evaluation = await this.evaluateBatch(
				flagKeys.map((flagKey) => ({ flagKey })),
			);

			const enabledFlags: Partial<Record<K, boolean>> = {};
			for (const response of evaluation.responses) {
				const key = response.booleanResponse?.flagKey as K | undefined;
				if (key) {
					enabledFlags[key] = response.booleanResponse?.enabled ?? false;
				}
			}

			return enabledFlags;
		} catch {
			return {};
		}
	}
}
